/**
 * 安装提示（Chromium 系的 `beforeinstallprompt`）。
 *
 * ⚠️ 这个事件**只会派发一次，而且很早**——早于 React 挂载。晚一步注册监听器就永远拿不到它，
 * 表现是"安卓上从来不弹安装提示"，且没有任何报错。所以本模块在被 import 的那一刻就
 * 立即抓取（入口文件里 import 一次即可），而不是等你调用某个 init。
 *
 * ⚠️ iOS / Safari 上**没有**这个事件，只能走手动引导（见 @pwaship/ui 的 InstallGuide）。
 * `available() === false` 不代表装不了，只代表要换一种引导方式。
 */
import { fail, failFrom, ok } from './result.js';
let deferred = null;
let installed = false;
const listeners = new Set();
function notify() {
    for (const l of listeners)
        l();
}
if (typeof window !== 'undefined') {
    window.addEventListener('beforeinstallprompt', (event) => {
        // 不 preventDefault 的话浏览器会用自己的 mini-infobar，我们就控制不了时机和文案
        event.preventDefault();
        deferred = event;
        notify();
    });
    window.addEventListener('appinstalled', () => {
        installed = true;
        deferred = null;
        notify();
    });
}
export const installPrompt = {
    available: () => deferred !== null,
    installedThisSession: () => installed,
    async prompt() {
        const event = deferred;
        if (!event) {
            // 没有事件可用：不是"失败"，是"这个平台/这个时刻要走手动引导"
            return fail('unsupported', 'beforeinstallprompt 不可用，请改用手动添加到主屏幕的引导');
        }
        try {
            await event.prompt();
            const { outcome } = await event.userChoice;
            // 事件是一次性的，无论结果如何都不能再用
            deferred = null;
            notify();
            return ok(outcome);
        }
        catch (e) {
            deferred = null;
            notify();
            return failFrom('failed', e);
        }
    },
    subscribe(listener) {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },
};
//# sourceMappingURL=install.js.map